/**
 * Step 1: Destination Type Selector
 * 
 * User selects how they want to receive funds:
 * - Bank Account (fiat) - Auto-convert to fiat
 * - Crypto Wallet (crypto) - Receive crypto directly
 * 
 * @module addresses/wizard
 */

import React from 'react';
import { Heading } from "@/components/ui/typography";
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import type { DestinationType } from './types';

interface DestinationTypeSelectorProps {
  value: DestinationType;
  onChange: (type: DestinationType) => void;
}

interface DestinationOption {
  type: DestinationType;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  benefits: string[];
  badge?: string;
  recommended?: boolean;
}

const destinationOptions: DestinationOption[] = [
  {
    type: 'fiat',
    icon: <SFIcon icon={sfIcons.landmark} size="xl" />,
    title: 'Bank Account',
    subtitle: 'Auto-convert crypto to fiat',
    benefits: [
      'Automatic conversion to USD or EUR',
      'Funds arrive in 1-2 business days',
      'Best for freelancers & businesses',
    ],
    badge: 'Most Popular',
    recommended: true,
  },
  {
    type: 'crypto',
    icon: <SFIcon icon={sfIcons.wallet} size="xl" />,
    title: 'Crypto Wallet',
    subtitle: 'Receive crypto directly',
    benefits: [
      'Keep funds as cryptocurrency',
      'Instant transfers',
      'Best for crypto-to-crypto flows',
    ],
  },
];

export const DestinationTypeSelector: React.FC<DestinationTypeSelectorProps> = ({
  value,
  onChange,
}) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <Heading level={2} className={cn("text-xl sm:text-heading-lg", semanticTokens.fg.default)}>
          How do you want to receive funds?
        </Heading>
        <p className={cn("text-sm sm:text-base", semanticTokens.fg.muted)}>
          Choose where your crypto payments will go
        </p>
      </div>

      {/* Options Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {destinationOptions.map((option) => {
          const isSelected = value === option.type;
          
          return (
            <button
              key={option.type}
              type="button"
              onClick={() => onChange(option.type)}
              className={cn(
                "relative p-6 rounded-xl border-2 text-left transition-all duration-240",
                "hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                "min-h-[200px] flex flex-col",
                isSelected 
                  ? "border-primary bg-primary/5 shadow-sm" 
                  : cn(semanticTokens.border.default, semanticTokens.bg.surface, "hover:border-primary/40")
              )}
              aria-pressed={isSelected}
            >
              {/* Badge */}
              {option.badge && (
                <span className={cn(
                  "absolute -top-2 right-4 px-2 py-0.5 text-xs font-medium rounded-full",
                  option.recommended 
                    ? "bg-primary text-primary-foreground" 
                    : cn(semanticTokens.bg.muted, semanticTokens.fg.muted)
                )}>
                  {option.badge}
                </span>
              )}

              {/* Icon */}
              <div className={cn(
                "mb-4 p-3 rounded-lg w-fit",
                isSelected ? "bg-primary/10 text-primary" : cn(semanticTokens.bg.muted, semanticTokens.fg.muted)
              )}>
                {option.icon}
              </div>

              {/* Content */}
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className={cn("text-lg font-semibold", semanticTokens.fg.default)}>
                    {option.title}
                  </h3>
                  <p className={cn("text-sm", semanticTokens.fg.muted)}>
                    {option.subtitle}
                  </p>
                </div>

                {/* Benefits */}
                <ul className="space-y-1.5">
                  {option.benefits.map((benefit, idx) => (
                    <li 
                      key={idx} 
                      className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}
                    >
                      <span className={cn("mt-0.5", semanticTokens.positive.accent)}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Selection indicator */}
              <div className={cn(
                "mt-4 pt-4 border-t flex items-center justify-between",
                isSelected ? "border-primary/30" : semanticTokens.border.subtle
              )}>
                <span className={cn(
                  "text-sm font-medium",
                  isSelected ? "text-primary" : semanticTokens.fg.muted
                )}>
                  {isSelected ? "Selected" : "Select"}
                </span>
                <SFIcon icon={sfIcons.next} size="sm" className={cn(
                  "transition-transform duration-240",
                  isSelected ? "text-primary translate-x-1" : semanticTokens.fg.disabled
                )} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Info box */}
      <div className={cn(
        "p-4 rounded-lg border",
        semanticTokens.info.bg,
        semanticTokens.info.border
      )}>
        <p className={cn("text-sm flex items-start gap-2", semanticTokens.info.fg)}>
          <SFIcon icon={sfIcons.info} size="sm" className="shrink-0 mt-0.5" />
          <span>
            <strong>Tip:</strong> You can create multiple addresses with different destinations. 
            Most users choose "Bank Account" to automatically receive fiat.
          </span>
        </p>
      </div>
    </div>
  );
};

export default DestinationTypeSelector;

